/**
 * material_finish — the RUNTIME half of the material-finish pass (2026-09-25,
 * visual-realism R1 + R6-grime; the offline half is
 * `tools/asset-pipeline/material-finish.mjs`).
 *
 * The offline tool bakes an ORM texture per finished asset: R = ambient
 * occlusion (three's `aoMap` — MeshStandardNodeMaterial reads `aoMap.r` and
 * occludes the INDIRECT diffuse + specular, `NodeMaterial.setupAmbientOcclusion`
 * → `PhysicalLightingModel.ambientOcclusion`), G = roughness, B = metal, and
 * packs curvature + cavity into the free A channel. This module reads that A
 * channel at PARSE, in one composed colour/roughness graph:
 *
 *   • MACRO GRIME (R6): world-space periodic value noise at 32 m + 8 m ×
 *     (occlusion, cavity, up-facing) darkens the albedo ≤ 12 % and roughens
 *     ≤ 0.15 — dirt settles in crevices and on ledges, never as a uniform film,
 *     and a town's buildings stop looking freshly printed;
 *   • SNOW IN CREVICES: the weather's snow layer (the engine
 *     AccumulationLayer's handle — its uniforms stay the drive) collects in the
 *     cavities first.
 *
 * WHY one composed graph instead of stacking the engine layer: the engine's
 * `applyAccumulationLayer` REPLACES `colorNode` with `mix(materialColor, …)`,
 * so anything set before it is lost and anything after it sits on top of the
 * snow. Composing here keeps grime under the snow. It also closes the engine
 * mask's coverage-0 leak: `threshold = 1 − coverage + jitter` with a negative
 * jitter puts snow on a flat roof in summer; the mask below is gated by the
 * coverage itself.
 *
 * FRAME LAW: the noise is sampled at ABSOLUTE world position — render-space
 * `positionWorld` + the origin taken modulo `GRIME_PERIOD_M` in doubles on the
 * CPU (`setGrimeOrigin`) — and the noise lattice is periodic with that period,
 * so the pattern neither swims at an origin rebase nor pops when the modulo wraps.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, If, float, vec3, vec4, uniform, texture, uv, positionWorld, positionLocal, normalWorld,
  materialColor, materialRoughness, mix, saturate, smoothstep, pow, floor, fract, sin, dot, mx_noise_float,
  materialMetalness,
} from 'three/tsl';
import { wetLayerFor, wetSurfacesMode } from './wet_layer.js'; // weather surfaces R3 (2026-09-30): the wet look, composed in the same graph

/** The noise lattice period (m): a multiple of both octave cells, so the origin modulo wraps seamlessly. */
export const GRIME_PERIOD_M = 4096;
export const GRIME_CELLS_M = [32, 8] as const;
/** At most this much darker / this much rougher where grime is full. */
export const GRIME_DARKEN = 0.12;
export const GRIME_ROUGHEN = 0.15;
/** ORM alpha: v·CURV_MAX packs 0.5 + 0.5·(convex − cavity); ≥ CURV_NODATA reads "no curvature" (an RGB-cooked LOD). */
export const CURV_MAX = 240;
export const CURV_NODATA = 0.97;
/** Snow reaches this much further into a full cavity (as a multiple of the coverage). */
export const SNOW_CREVICE = 0.6;

/** origin mod period (vec3), written per frame by `setGrimeOrigin`. */
export const uGrimeOriginMod = uniform(new THREE.Vector3());
/** 0 = off … 1 = full (the tier / menu drive, live). */
export const uGrimeStrength = uniform(1);

export const materialFinishStats = { finished: 0, grimed: 0, snowComposed: 0, keyed: 0, wet: 0 };

/** Origin (ABSOLUTE metres, doubles) → the modulo the shader adds to render-space positions. */
export function setGrimeOrigin(ox: number, oy: number, oz: number): void {
  const m = (v: number): number => ((v % GRIME_PERIOD_M) + GRIME_PERIOD_M) % GRIME_PERIOD_M;
  (uGrimeOriginMod.value as THREE.Vector3).set(m(ox), m(oy), m(oz));
}

// ─── CPU mirrors (the harness pins the TSL below against these) ─────────────

export function decodeCurvatureCpu(a: number): { convex: number; cavity: number; valid: boolean } {
  if (a >= CURV_NODATA) return { convex: 0, cavity: 0, valid: false };
  const v = (a * 255) / CURV_MAX;
  return { convex: Math.min(1, Math.max(0, 2 * v - 1)), cavity: Math.min(1, Math.max(0, 1 - 2 * v)), valid: true };
}
/** Where dirt settles, 0..1: the macro noise shaped, times occlusion / cavity / up-facing. */
export function grimeMaskCpu(noise: number, ao: number, cavity: number, up: number): number {
  const n = smooth(0.35, 0.8, noise);
  return n * Math.min(1, Math.max(0, (1 - ao) * 0.7 + cavity * 0.8 + Math.max(0, up) * 0.3));
}
/** The snow mask with the crevice term and the coverage gate (coverage 0 ⇒ no snow, anywhere). */
export function snowMaskCpu(up: number, coverage: number, upBias: number, noise01: number, noiseStrength: number, cavity: number): number {
  const upness = Math.pow(Math.max(0, up), upBias);
  const threshold = 1 - coverage * (1 + SNOW_CREVICE * cavity) + (noise01 - 0.5) * noiseStrength;
  return smooth(threshold, threshold + 0.15, upness) * smooth(0, 0.05, coverage);
}
function smooth(e0: number, e1: number, x: number): number { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); }

// ─── TSL ────────────────────────────────────────────────────────────────────

/** A sin-fract hash of a (non-negative, integer-valued) lattice cell — sign-safe by construction (shaders.md §0). */
const hash3 = (c: ReturnType<typeof vec3>): ReturnType<typeof float> => fract(sin(dot(c, vec3(12.9898, 78.233, 37.719))).mul(43758.5453));
/** x mod y for floats, explicit (WGSL `%` truncates toward zero). */
const fmod = (x: any, y: number): any => x.sub(floor(x.div(y)).mul(y));
/** Periodic trilinear value noise: cell `cell` m, lattice period GRIME_PERIOD_M / cell cells. */
function periodicValueNoise(p: any, cell: number): any {
  const n = GRIME_PERIOD_M / cell;
  const q = p.div(cell); const i = floor(q); const f = fract(q);
  const s = f.mul(f).mul(float(3).sub(f.mul(2)));
  const corner = (dx: number, dy: number, dz: number): any => hash3(fmod(i.add(vec3(dx, dy, dz)), n) as never);
  const x00 = mix(corner(0, 0, 0), corner(1, 0, 0), s.x), x10 = mix(corner(0, 1, 0), corner(1, 1, 0), s.x);
  const x01 = mix(corner(0, 0, 1), corner(1, 0, 1), s.x), x11 = mix(corner(0, 1, 1), corner(1, 1, 1), s.x);
  return mix(mix(x00, x10, s.y), mix(x01, x11, s.y), s.z);
}

interface SnowHandle { color: { value: THREE.Vector3 }; roughness: { value: number }; coverage: { value: number }; upBias: { value: number }; noiseScale: { value: number }; noiseStrength: { value: number } }
type StdMat = THREE.MeshStandardMaterial & { colorNode?: unknown; roughnessNode?: unknown; userData: Record<string, unknown> };

/** True when the material carries a finish bake (its ORM alpha is curvature). */
export function isFinished(mat: THREE.Material): boolean {
  const m = mat as StdMat;
  return !!m.userData?.matFinish && !!m.aoMap;
}

/**
 * Compose colour + roughness for one standard/physical material: grime (when
 * `grime`), then the snow layer on top (when `snow` — the engine handle whose
 * uniforms the weather drives). Returns false when nothing was applied.
 */
export function composeFinishLayers(material: THREE.Material, opts: { grime: boolean; snow: SnowHandle | null }): boolean {
  const mat = material as StdMat;
  if (!opts.grime && !opts.snow && wetSurfacesMode() !== 'all') return false; // weather surfaces R3: the wet layer alone is reason enough
  const finished = isFinished(mat);
  const orm = finished ? texture(mat.aoMap as THREE.Texture, uv((mat.aoMap as THREE.Texture).channel ?? 0)) : null;
  const curv = Fn(() => {
    if (!orm) return vec3(1, 0, 0); // (ao, convex, cavity) of an unfinished surface
    const s = vec4(orm as never);
    const valid = float(1).sub(smoothstep(CURV_NODATA - 0.01, CURV_NODATA, s.a));
    const v = s.a.mul(255 / CURV_MAX);
    return vec3(s.r, saturate(v.mul(2).sub(1)).mul(valid), saturate(float(1).sub(v.mul(2))).mul(valid));
  })();
  const grimeMask = Fn(() => {
    if (!opts.grime) return float(0);
    const p = positionWorld.add(uGrimeOriginMod);
    const noise = periodicValueNoise(p, GRIME_CELLS_M[0]).mul(0.6).add(periodicValueNoise(p, GRIME_CELLS_M[1]).mul(0.4));
    const n = smoothstep(0.35, 0.8, noise);
    const up = saturate(normalWorld.y);
    return n.mul(saturate(float(1).sub(curv.x).mul(0.7).add(curv.z.mul(0.8)).add(up.mul(0.3)))).mul(uGrimeStrength);
  })();
  // WEATHER SURFACES R3: the wet look (Lagarde's absorbed darkening + surface film), UNDER the snow — null when the flag is off
  const wet = wetLayerFor(mat, orm ? vec4(orm as never).b.mul(materialMetalness) : materialMetalness, finished);
  if (!opts.grime && !opts.snow && !wet) return false;
  const snowMask = Fn(() => {
    if (!opts.snow) return float(0);
    const h = opts.snow as unknown as Record<string, any>;
    const upness = pow(saturate(normalWorld.y), h.upBias);
    // PERF (shader sweep C, 2026-10-06): the mask ends × smoothstep(0, 0.05, coverage), EXACTLY 0 with no snow — yet the
    // 3D Perlin (8 hashes) ran on every fragment of every structure, prop and decor, every frame of the year. Gated on the
    // UNIFORM coverage. The 0.5 default is the noise's mean — any finite value works, the result is × 0 there.
    const noise = float(0.5).toVar();
    If(h.coverage.greaterThan(0), () => { noise.assign(mx_noise_float(positionLocal.mul(h.noiseScale)).mul(0.5).add(0.5)); });
    const threshold = float(1).sub(h.coverage.mul(float(1).add(curv.z.mul(SNOW_CREVICE)))).add(noise.sub(0.5).mul(h.noiseStrength));
    return smoothstep(threshold, threshold.add(0.15), upness).mul(smoothstep(0, 0.05, h.coverage));
  })();
  mat.colorNode = Fn(() => {
    const base = vec4(materialColor);
    const grimed0 = base.rgb.mul(float(1).sub(grimeMask.mul(GRIME_DARKEN)));
    const grimed = wet ? wet.color(grimed0) : grimed0;
    const out = opts.snow ? mix(grimed, (opts.snow as unknown as Record<string, any>).color, snowMask) : grimed;
    return vec4(out, base.a);
  })();
  mat.roughnessNode = Fn(() => {
    const r0 = saturate(float(materialRoughness).add(grimeMask.mul(GRIME_ROUGHEN)));
    const r = wet ? wet.rough(r0) : r0;
    return opts.snow ? mix(r, (opts.snow as unknown as Record<string, any>).roughness, snowMask) : r;
  })();
  mat.needsUpdate = true;
  if (finished) materialFinishStats.finished++;
  if (opts.grime) materialFinishStats.grimed++;
  if (opts.snow) materialFinishStats.snowComposed++;
  if (wet) { materialFinishStats.wet++; mat.userData.wetClass = wet.cls; }
  return true;
}

/**
 * THE CLASSIC-KEY LAW for the parsed asset materials (materials agent, 2026-09-26; the ground agent's law, shaders.md
 * §0). GLTFLoader hands out CLASSIC `MeshStandardMaterial`s and the parse passes hang node graphs on them (the snow
 * accumulation, this module's grime / snow compose — which CAPTURES the material's own ORM texture — specular AA's
 * normal map, anisotropy, retroreflection, cloud shadows, the detail surface). three keys a classic material's render
 * object by `customProgramCacheKey()` (`onBeforeCompile`'s text) + its scalars, where a node property keys as "{}"
 * (RenderObject.getMaterialCacheKey): every such material on a PLAIN mesh (an InstancedMesh is keyed per object) shared
 * the FIRST build's graph AND bindings — the second finished body grimed with the first body's ORM, a grime-only graph
 * could draw the grime+snow one, and a slot changed after the first render never rebuilt. This keys the graph the way
 * three keys a NodeMaterial's (`NodeMaterial.customProgramCacheKey`: every node slot's `getCacheKey()`), LAZILY — so
 * the layers applied after this call are in the key too, and the same graph object shared by two materials still
 * shares one build. Idempotent; a NodeMaterial is left alone (it already keys its graph).
 */
export function keyClassicNodeGraph(material: THREE.Material): boolean {
  const m = material as unknown as { isNodeMaterial?: boolean; customProgramCacheKey: (() => string) & { classicNodeKey?: true } };
  if (m.isNodeMaterial || m.customProgramCacheKey?.classicNodeKey) return false;
  const base = THREE.Material.prototype.customProgramCacheKey;
  const fn = function keyed(this: Record<string, unknown>): string {
    let k = base.call(this as unknown as THREE.Material);
    for (const p of Object.keys(this)) {
      if (!p.endsWith('Node')) continue;
      const n = this[p] as { isNode?: boolean; getCacheKey?: () => number } | null | undefined;
      if (n && n.isNode === true && typeof n.getCacheKey === 'function') k += `|${p}:${n.getCacheKey()}`;
    }
    return k;
  } as (() => string) & { classicNodeKey?: true };
  fn.classicNodeKey = true;
  m.customProgramCacheKey = fn;
  materialFinishStats.keyed++;
  return true;
}
